import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import lodashTimes from 'lodash/fp/times';
import lodashCompact from 'lodash/fp/compact';
import lodashGroupBy from 'lodash/fp/groupBy';

import { createFormFieldsByIdGetter } from 'client/helpers/formikHelpers';

import {
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  RENTAL_APPLICATION_PAYSTUB_FILES_COUNT,
  RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT,
} from 'common/householdApplication/incomeHelpers';
import { INCOME_VERIFICATION_STATUS_FAILED } from 'common/incomeVerificationConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormikField from 'client/components/SrFormikField';
import SrButtonDeprecated from 'client/components/SrButtonDeprecated';
import SrButton, {
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

import './SrUploadDocumentsApplicationFlow.less';

const UPLOAD_DOCUMENT_PROGRESS_COMPLETED = 'completed';
const UPLOAD_DOCUMENT_PROGRESS_IN_PROGRESS = 'inProgress';
const INCOME_TYPE_MAPPING = (formikBag) => ({
  [PROOF_OF_INCOME_PAYTSTUBS]: formikBag.values.paystubPhotos,
  [PROOF_OF_INCOME_BANK_STATEMENTS]: formikBag.values.bankStatementFileUrls,
  [PROOF_OF_INCOME_OFFER_LETTER]: [formikBag.values.offerLetterFileUrl],
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: formikBag.values.additionalProofOfIncomePhotos || 1,
});
export const getIncomeSectionDocumentsCountByType = (type, formikBag) => {
  const incomeType = INCOME_TYPE_MAPPING(formikBag);
  const totalDocuments = lodashCompact(incomeType[type]).length;

  return totalDocuments;
};
const getIncomeSectionDocumentsUploadProgress = ({
  uploadDocumentType,
  formikBag,
  incomeVerificationEnabled,
  documentsWithErrors,
}) => {
  const totalDocuments = getIncomeSectionDocumentsCountByType(uploadDocumentType, formikBag);

  if (documentsWithErrors[uploadDocumentType]) {
    return UPLOAD_DOCUMENT_PROGRESS_IN_PROGRESS;
  }

  if (uploadDocumentType === PROOF_OF_INCOME_PAYTSTUBS) {
    // If the income verification provider is already linked mark as completed the paystubs
    if (
      totalDocuments === RENTAL_APPLICATION_PAYSTUB_FILES_COUNT ||
      getIncomeVerificationLinked(formikBag, incomeVerificationEnabled)
    ) {
      return UPLOAD_DOCUMENT_PROGRESS_COMPLETED;
    }
    if (totalDocuments > 0) {
      return UPLOAD_DOCUMENT_PROGRESS_IN_PROGRESS;
    }
  } else if (uploadDocumentType === PROOF_OF_INCOME_BANK_STATEMENTS) {
    if (totalDocuments === RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT) {
      return UPLOAD_DOCUMENT_PROGRESS_COMPLETED;
    }
    if (totalDocuments > 0) {
      return UPLOAD_DOCUMENT_PROGRESS_IN_PROGRESS;
    }
  } else if (uploadDocumentType === PROOF_OF_INCOME_OFFER_LETTER) {
    if (totalDocuments === 1) {
      return UPLOAD_DOCUMENT_PROGRESS_COMPLETED;
    }
  } else if (uploadDocumentType === PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS) {
    if (totalDocuments === (formikBag.values.additionalProofOfIncomePhotosCount || 1)) {
      return UPLOAD_DOCUMENT_PROGRESS_COMPLETED;
    }
    if (totalDocuments > 0) {
      return UPLOAD_DOCUMENT_PROGRESS_IN_PROGRESS;
    }
  }

  return '';
};
const getIncomeSectionTitleDocumentsCountByType = (type, formikBag) => {
  const totalDocuments = getIncomeSectionDocumentsCountByType(type, formikBag);
  const totalDocumentsText = totalDocuments > 0 ? `(${totalDocuments})` : '';

  return totalDocumentsText;
};
export const getIncomeVerificationLinked = (formikBag, incomeVerificationEnabled) => {
  if (!incomeVerificationEnabled) {
    return false;
  }

  return (
    formikBag.values.incomeVerificationVOIE &&
    formikBag.values.incomeVerificationVOIE.status !== INCOME_VERIFICATION_STATUS_FAILED
  );
};

const UploadDocumentsItemContainer = ({
  title,
  subtext,
  formikBag,
  formFields,
  addDocumentType,
  subtextCompleted,
  uploadDocumentType,
  disabledUploadDocuments,
  incomeVerificationEnabled,
  subTextColor = 'grey',
  documentsWithErrors,
  incomeVerificationFormFieldErrorsByGroup,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const uploadedProgress = getIncomeSectionDocumentsUploadProgress({
    uploadDocumentType,
    formikBag,
    incomeVerificationEnabled,
    documentsWithErrors,
  });
  const totalDocumentsUploaded = getIncomeSectionTitleDocumentsCountByType(
    uploadDocumentType,
    formikBag,
  );
  const toggleOpen = () => {
    if (disabledUploadDocuments) {
      return;
    }

    setIsOpen(!isOpen);
  };

  return (
    <div styleName="uploadDocumentsItemContainer">
      <button onClick={toggleOpen} type="button" styleName="uploadDocumentsItemHeader">
        <div styleName={classNames('progressStatusDot', uploadedProgress)} />
        <div styleName="uploadDocumentsItemTitleContainer">
          <SrText
            weight="600"
            fontSize="24px"
            lineHeight="35px"
            font="whitney-sc"
            letterSpacing="0.59px"
          >
            {title} {totalDocumentsUploaded}
          </SrText>
          <SrText
            weight="400"
            fontSize="16px"
            lineHeight="20px"
            font="whitney"
            color={subTextColor}
          >
            {uploadedProgress === UPLOAD_DOCUMENT_PROGRESS_COMPLETED ? subtextCompleted : subtext}
          </SrText>
        </div>
        {!disabledUploadDocuments && (
          <div styleName="uploadDocumentsItemIcon">
            {isOpen ? (
              <img src="/images/minus-medium-gray-2.svg" alt="Minus" />
            ) : (
              <img src="/images/plus-medium-grey.svg" alt="Plus" />
            )}
          </div>
        )}
      </button>
      <div
        className="SrFormFieldGroupContent"
        styleName={classNames(
          'uploadDocumentsItem',
          isOpen ? 'uploadDocumentsItemOpen' : 'uploadDocumentsItemClose',
        )}
      >
        {formFields.map((formField) => {
          const formFieldWithErrors = {
            ...formField,
            properties: {
              ...(formField.properties || {}),
            },
          };

          const incomeVerificationFormFieldErrors =
            incomeVerificationFormFieldErrorsByGroup[formField.id] || [];

          if (incomeVerificationFormFieldErrors.length) {
            formFieldWithErrors.subtitle = (
              <SrBox margin="10px">
                <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE} fontFamily="Whitney">
                  {incomeVerificationFormFieldErrors.map((stepError) => (
                    <div key={`${stepError.title}-${stepError.fieldId}-${stepError.message}`}>
                      {stepError.message}
                      <br />
                    </div>
                  ))}
                </SrErrorMessage>
              </SrBox>
            );
          }

          return (
            <SrFormikField
              key={formField.id}
              formField={formFieldWithErrors}
              formikBag={formikBag}
            />
          );
        })}
        {addDocumentType && (
          <SrBox marginBottom="16px">
            <SrButtonDeprecated
              color="blue300"
              bgColor="grey200"
              borderRadius={5}
              text="+ Add additional proof of income"
              onClick={() => {
                formikBag.setFieldValue(
                  addDocumentType,
                  formikBag.values[addDocumentType] ? formikBag.values[addDocumentType] + 1 : 2,
                );
              }}
            />
          </SrBox>
        )}
      </div>
    </div>
  );
};

const SrUploadDocumentsApplicationFlow = ({
  formikBag,
  formFields,
  onClose,
  incomeVerificationEnabled,
  incomeVerificationFormFieldErrors,
}) => {
  const documentsWithErrors = incomeVerificationFormFieldErrors.reduce((memo, documentError) => {
    memo[documentError.proofOfIncomeType] = true;
    return memo;
  }, {});
  const incomeVerificationFormFieldErrorsByGroup = lodashGroupBy(
    'fieldGroup',
    incomeVerificationFormFieldErrors,
  );

  const getFormFieldsById = createFormFieldsByIdGetter(formFields);
  const incomeVerificationLinked = getIncomeVerificationLinked(
    formikBag,
    incomeVerificationEnabled,
  );
  const paystubSubText = !incomeVerificationLinked
    ? `${RENTAL_APPLICATION_PAYSTUB_FILES_COUNT} Paystubs need`
    : 'Payroll linked!';
  const paystubSubTextCompleted = !incomeVerificationLinked
    ? `${RENTAL_APPLICATION_PAYSTUB_FILES_COUNT} Paystubs uploaded`
    : 'Payroll linked!';
  const incomeSections = [
    {
      title: 'PAYSTUBS',
      subtext: paystubSubText,
      subtextCompleted: paystubSubTextCompleted,
      subTextColor: paystubSubTextCompleted === 'Payroll linked!' && 'green',
      formFields: getFormFieldsById([
        ...lodashTimes(
          (index) => `paystubPhotos[${index}]`,
          RENTAL_APPLICATION_PAYSTUB_FILES_COUNT,
        ),
      ]),
      uploadDocumentType: PROOF_OF_INCOME_PAYTSTUBS,
      disabledUploadDocuments: incomeVerificationLinked,
    },
    {
      title: 'BANK STATEMENTS',
      subtext: `${RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT} Bank Statements needed`,
      subtextCompleted: `${RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT} Bank Statements uploaded`,
      formFields: getFormFieldsById([
        ...lodashTimes(
          (index) => `bankStatementFileUrls[${index}]`,
          RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT,
        ),
      ]),
      uploadDocumentType: PROOF_OF_INCOME_BANK_STATEMENTS,
    },
    {
      title: 'OFFER LETTER',
      subtext: 'If recently hired',
      subtextCompleted: 'Offer letter uploaded',
      formFields: getFormFieldsById(['offerLetterFileUrl']),
      uploadDocumentType: PROOF_OF_INCOME_OFFER_LETTER,
    },
    {
      title: 'OTHER',
      subtext: 'Child support, government benefits, etc.',
      subtextCompleted: 'Other docs uploaded',
      formFields: getFormFieldsById([
        ...lodashTimes(
          (index) => `additionalProofOfIncomePhotos[${index}]`,
          formikBag.values.additionalProofOfIncomePhotosCount || 1,
        ),
      ]),
      uploadDocumentType: PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
      addDocumentType: 'additionalProofOfIncomePhotosCount',
    },
  ];

  return (
    <div styleName="uploadDocumentsModalPage">
      <div styleName="uploadDocumentsContainer">
        <div styleName="headerUploadDocumentsModalPage">
          <div styleName="headerWrapper">
            <div styleName="navbarRentalApplication">
              <button styleName="backButton" onClick={onClose} type="button" />
              <div styleName="navbarTitleWrapper">
                <SrText weight="bold" font="whitney-sc">
                  upload documents
                </SrText>
              </div>
            </div>
          </div>
        </div>
        <div styleName="main">
          <div styleName="infoBanner">
            <span>💡 TIP</span>
            Upload as much as possible to improve your odds of getting accepted. These are not all
            required.
          </div>
          <div styleName="documentSectionsContainer">
            {incomeSections.map((incomeSection) => (
              <Fragment key={incomeSection.title}>
                <UploadDocumentsItemContainer
                  {...incomeSection}
                  formikBag={formikBag}
                  incomeVerificationEnabled={incomeVerificationEnabled}
                  documentsWithErrors={documentsWithErrors}
                  incomeVerificationFormFieldErrorsByGroup={
                    incomeVerificationFormFieldErrorsByGroup
                  }
                />
                <SrSeparator opacity="0.1" backgroundColor="#7B8488" />
              </Fragment>
            ))}
          </div>
        </div>
        <div styleName="bottomFixedContainer">
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            heightType={SR_BUTTON_HEIGHT_TALL}
            hasBoxShadow
            minWidth="100%"
            onClick={onClose}
          >
            Save
          </SrButton>
        </div>
      </div>
    </div>
  );
};

SrUploadDocumentsApplicationFlow.propTypes = {
  onClose: PropTypes.func.isRequired,
  formikBag: PropTypes.object.isRequired,
  formFields: PropTypes.array.isRequired,
  incomeVerificationEnabled: PropTypes.bool.isRequired,
  documentErrors: PropTypes.array.isRequired,
};

SrUploadDocumentsApplicationFlow.displayName = 'SrUploadDocumentsApplicationFlow';

export default SrUploadDocumentsApplicationFlow;
